
import { clampNumber } from "./lcaTypes";

const KG_PER_T = 1000;

export function toKg(value, unit) {
  const v = clampNumber(value, 0);
  if (unit === "kg") return v;
  if (unit === "t") return v * KG_PER_T;
  return null;
}

export function fromKgToDeclaredUnitKg(valueKg) {
  return valueKg;
}

export function convertInputToDeclaredQuantity({
  inputValue,
  inputUnit,
  declaredUnit,
  densityKgPerM3,
  massPerAreaKgPerM2,
  thicknessM,
  customFactor
}) {
  const v = clampNumber(inputValue, 0);
  const d = clampNumber(densityKgPerM3, 0);
  const a = clampNumber(massPerAreaKgPerM2, 0);
  const t = clampNumber(thicknessM, 0);
  const f = clampNumber(customFactor, 0);

  const result = {
    ok: false,
    declaredQuantity: 0,
    notes: [],
    needs: {
      density: false,
      thickness: false,
      customFactor: false
    }
  };

  if (!declaredUnit) {
    result.notes.push("No declared unit set on EPD");
    return result;
  }

  // Direct matches
  if (inputUnit === declaredUnit) {
    result.ok = true;
    result.declaredQuantity = v;
    return result;
  }

  // pcs can only be pcs in MVP unless a custom factor is provided
  if (declaredUnit === "pcs") {
    if (f > 0) {
      result.ok = true;
      result.declaredQuantity = v * f;
      result.notes.push("Used custom factor to convert to pcs");
      return result;
    }
    result.needs.customFactor = true;
    result.notes.push("Needs custom factor to convert to pcs");
    return result;
  }

  // m3 declared
  if (declaredUnit === "m3") {
    if (inputUnit === "kg" || inputUnit === "t") {
      const kg = toKg(v, inputUnit);
      if (kg == null) {
        result.notes.push("Unsupported conversion to m³");
        return result;
      }
      if (!(d > 0)) {
        result.needs.density = true;
        result.notes.push("Needs density to convert kg to m³");
        return result;
      }
      result.ok = true;
      result.declaredQuantity = kg / d;
      return result;
    }

    if (inputUnit === "m2") {
      if (a > 0) {
        result.ok = true;
        result.declaredQuantity = v * a;
        result.notes.push("Used areal density to convert mA? to kg");
        return result;
      }
      if (!(t > 0)) {
        result.needs.thickness = true;
        result.notes.push("Needs thickness to convert m² to m³");
        return result;
      }
      result.ok = true;
      result.declaredQuantity = v * t;
      return result;
    }

    if (f > 0) {
      result.ok = true;
      result.declaredQuantity = v * f;
      result.notes.push("Used custom factor to convert to m³");
      return result;
    }

    result.needs.customFactor = true;
    result.notes.push("Needs custom factor for this conversion to m³");
    return result;
  }

  // m2 declared
  if (declaredUnit === "m2") {
    if (inputUnit === "m3") {
      if (!(t > 0)) {
        result.needs.thickness = true;
        result.notes.push("Needs thickness to convert m³ to m²");
        return result;
      }
      result.ok = true;
      result.declaredQuantity = v / t;
      return result;
    }

    if (inputUnit === "kg" || inputUnit === "t") {
      const kg = toKg(v, inputUnit);
      if (kg == null) return result;
      if (a > 0) {
        result.ok = true;
        result.declaredQuantity = kg / a;
        result.notes.push("Used areal density to convert kg to m2");
        return result;
      }
      if (!(d > 0)) {
        result.needs.density = true;
        result.notes.push("Needs density to convert kg to m²");
        return result;
      }
      if (!(t > 0)) {
        result.needs.thickness = true;
        result.notes.push("Needs thickness to convert kg to m²");
        return result;
      }
      const m3 = kg / d;
      result.ok = true;
      result.declaredQuantity = m3 / t;
      return result;
    }

    if (f > 0) {
      result.ok = true;
      result.declaredQuantity = v * f;
      result.notes.push("Used custom factor to convert to m²");
      return result;
    }

    result.needs.customFactor = true;
    result.notes.push("Needs custom factor for this conversion to m²");
    return result;
  }

  // kg declared
  if (declaredUnit === "kg") {
    if (inputUnit === "t" || inputUnit === "kg") {
      const kg = toKg(v, inputUnit);
      result.ok = true;
      result.declaredQuantity = fromKgToDeclaredUnitKg(kg);
      return result;
    }

    if (inputUnit === "m3") {
      if (!(d > 0)) {
        result.needs.density = true;
        result.notes.push("Needs density to convert m³ to kg");
        return result;
      }
      result.ok = true;
      result.declaredQuantity = v * d;
      return result;
    }

    if (inputUnit === "m2") {
      if (a > 0) {
        result.ok = true;
        result.declaredQuantity = v * a;
        result.notes.push("Used areal density to convert m2 to kg");
        return result;
      }
      if (!(t > 0)) {
        result.needs.thickness = true;
        result.notes.push("Needs thickness to convert m² to kg");
        return result;
      }
      if (!(d > 0)) {
        result.needs.density = true;
        result.notes.push("Needs density to convert m² to kg");
        return result;
      }
      const m3 = v * t;
      result.ok = true;
      result.declaredQuantity = m3 * d;
      return result;
    }

    if (f > 0) {
      result.ok = true;
      result.declaredQuantity = v * f;
      result.notes.push("Used custom factor to convert to kg");
      return result;
    }

    result.needs.customFactor = true;
    result.notes.push("Needs custom factor for this conversion to kg");
    return result;
  }

  // Fallback
  if (f > 0) {
    result.ok = true;
    result.declaredQuantity = v * f;
    result.notes.push("Used custom factor fallback");
    return result;
  }

  result.needs.customFactor = true;
  result.notes.push("Needs custom factor fallback");
  return result;
}
